import { styled, keyframes } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

// ── Animation keyframes ──

export const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(2px); }
`;

export const checkDraw = keyframes`
  from { stroke-dashoffset: 30; }
  to { stroke-dashoffset: 0; }
`;

export const circleDraw = keyframes`
  from { stroke-dashoffset: 170; }
  to { stroke-dashoffset: 0; }
`;

export const submitPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ── Shared MUI TextField styling ──

const textFieldStyles = (palette, hasError) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '15px',
    lineHeight: 1.5,
    color: palette.textPrimary,
    backgroundColor: palette.inputBg,
    borderRadius: '10px',
    transition: 'box-shadow 200ms ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? palette.errorText : palette.inputBorder,
    transition: 'border-color 200ms ease',
  },
  '& .MuiInputBase-root:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? palette.errorText : palette.inputBorderHover,
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? palette.errorText : palette.inputBorderFocus,
    borderWidth: '1px',
  },
  '& .MuiInputBase-root.Mui-focused': {
    boxShadow: hasError
      ? `0 0 0 3px ${palette.focusRingError}`
      : `0 0 0 3px ${palette.focusRing}`,
  },
  '& .MuiInputBase-root.Mui-disabled': {
    opacity: 0.5,
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    '&::placeholder': {
      color: '#a1a1aa',
      opacity: 1,
    },
  },
  // Single-line height
  '& .MuiInputBase-root:not(.MuiInputBase-multiline)': {
    height: '44px',
  },
  // Multiline padding
  '& .MuiInputBase-multiline': {
    padding: 0,
    '& .MuiInputBase-inputMultiline': {
      padding: '10px 14px',
    },
  },
});

// ── StyledTextField — replaces StyledInput & StyledTextarea ──

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !['palette', 'hasError'].includes(prop),
})(({ palette, hasError }) => ({
  ...textFieldStyles(palette, hasError),
}));

// ── StyledSelectField — replaces StyledSelect ──

export const StyledSelectField = styled(TextField, {
  shouldForwardProp: (prop) => !['palette', 'hasError'].includes(prop),
})(({ palette, hasError }) => ({
  ...textFieldStyles(palette, hasError),
  '& .MuiSelect-select': {
    padding: '10px 14px',
  },
  '& .MuiSelect-icon': {
    color: palette.textSecondary,
    right: '10px',
  },
}));

// ── Animated field wrapper ──

export const AnimatedFieldWrapper = styled('div', {
  shouldForwardProp: (prop) => !['index', 'palette'].includes(prop),
})(({ index = 0, palette }) => ({
  opacity: 0,
  animation: `${fadeSlideUp} 300ms ease-out forwards`,
  animationDelay: `${index * 30}ms`,
  padding: '20px 0',
  borderRadius: '8px',
  transition: 'background-color 150ms ease',
  '&:hover': {
    backgroundColor: palette?.fieldHoverBg || 'rgba(0,0,0,0.015)',
  },
}));

// ── Styled Radio — sleek custom radio ──

export const StyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => prop !== 'palette',
})(({ palette }) => ({
  padding: 0,
  color: palette.chipBorder,
  transition: 'color 200ms ease',
  '&.Mui-checked': {
    color: palette.selectedBorder,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

// ── Radio option card ──

export const StyledRadioOption = styled('label', {
  shouldForwardProp: (prop) => !['palette', 'selected'].includes(prop),
})(({ palette, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 14px',
  borderRadius: '10px',
  border: `1px solid ${selected ? palette.selectedBorder : palette.inputBorder}`,
  backgroundColor: selected ? palette.selectedBg : 'transparent',
  cursor: 'pointer',
  transition: 'border-color 200ms ease, background-color 150ms ease',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: palette.fieldHoverBg,
    borderColor: selected ? palette.selectedBorder : palette.inputBorderHover,
  },
}));

// ── Styled Checkbox — sleek custom checkbox ──

export const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== 'palette',
})(({ palette }) => ({
  padding: 0,
  color: palette.chipBorder,
  borderRadius: '4px',
  transition: 'color 200ms ease',
  '&.Mui-checked': {
    color: palette.selectedBorder,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

// ── Checkbox option card ──

export const StyledCheckboxOption = styled('label', {
  shouldForwardProp: (prop) => !['palette', 'selected'].includes(prop),
})(({ palette, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 14px',
  borderRadius: '10px',
  border: `1px solid ${selected ? palette.selectedBorder : palette.inputBorder}`,
  backgroundColor: selected ? palette.selectedBg : 'transparent',
  cursor: 'pointer',
  transition: 'border-color 200ms ease, background-color 150ms ease',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: palette.fieldHoverBg,
    borderColor: selected ? palette.selectedBorder : palette.inputBorderHover,
  },
}));

// ── Upload drop zone ──

export const StyledUploadZone = styled('div', {
  shouldForwardProp: (prop) => !['palette', 'isDragOver'].includes(prop),
})(({ palette, isDragOver }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '32px 24px',
  borderRadius: '12px',
  border: `2px dashed ${isDragOver ? palette.accent : palette.chipBorder}`,
  backgroundColor: isDragOver ? palette.selectedBg : 'transparent',
  cursor: 'pointer',
  transition: 'border-color 200ms ease, background-color 200ms ease',
  '&:hover': {
    borderColor: palette.accent,
    backgroundColor: palette.fieldHoverBg,
  },
}));

// ── Skeleton field placeholder ──

export const SkeletonField = styled('div', {
  shouldForwardProp: (prop) => prop !== 'palette',
})(({ palette }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '20px 0',
  '& .skel-label': {
    width: '120px',
    height: '14px',
    borderRadius: '4px',
    backgroundColor: palette?.chipBg || '#f4f4f5',
    animation: `${pulse} 1500ms ease-in-out infinite`,
  },
  '& .skel-input': {
    width: '100%',
    height: '44px',
    borderRadius: '10px',
    backgroundColor: palette?.chipBg || '#f4f4f5',
    animation: `${pulse} 1500ms ease-in-out infinite`,
    animationDelay: '200ms',
  },
}));

// ── Submit button (MUI Button) ──

export const StyledSubmitButton = styled(Button, {
  shouldForwardProp: (prop) => !['palette', 'isLoading'].includes(prop),
})(({ palette, isLoading }) => ({
  width: '100%',
  height: '48px',
  borderRadius: '10px',
  backgroundColor: palette.accent,
  color: palette.surface,
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  textTransform: 'none',
  cursor: isLoading ? 'wait' : 'pointer',
  transition: 'transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease',
  ...(isLoading
    ? {
        animation: `${submitPulse} 1500ms ease-in-out infinite`,
      }
    : {
        '&:hover:not(:disabled)': {
          backgroundColor: palette.accent,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        '&:active:not(:disabled)': {
          transform: 'translateY(0)',
        },
      }),
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
    backgroundColor: palette.accent,
    color: palette.surface,
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${palette.focusRing}`,
  },
}));

// ── File chip (MUI Chip) ──

export const FileChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'palette',
})(({ palette }) => ({
  height: 'auto',
  borderRadius: '999px',
  backgroundColor: palette.chipBg,
  color: palette.chipText,
  fontSize: '13px',
  lineHeight: 1.4,
  maxWidth: '200px',
  border: `1px solid ${palette.chipBorder}`,
  '& .MuiChip-label': {
    padding: '4px 10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

// ── Ghost add button (MUI Button) ──

export const GhostAddButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'palette',
})(({ palette }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  padding: '10px',
  border: `1px dashed ${palette.chipBorder}`,
  borderRadius: '10px',
  backgroundColor: 'transparent',
  color: palette.textSecondary,
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  textTransform: 'none',
  cursor: 'pointer',
  transition: 'border-color 200ms ease, color 200ms ease, background-color 150ms ease',
  '&:hover': {
    borderColor: palette.accent,
    color: palette.textPrimary,
    backgroundColor: palette.fieldHoverBg,
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${palette.focusRing}`,
  },
}));

// ── Legacy exports (kept for backward compat during transition) ──
// StyledInput, StyledTextarea, StyledSelect, RadioIndicator, CheckboxIndicator
// are no longer exported — use StyledTextField, StyledSelectField, StyledRadio, StyledCheckbox instead.
